import '../styles/v3-chrome.css';
import React from 'react';
import { motion } from 'framer-motion';
import { Smiley, Floppy, IconWindow, AchievementSticker } from './motifs';

const portraitBaseUrl = `${process.env.PUBLIC_URL}/assets/images`;
const portraitUrl480 = `${portraitBaseUrl}/vijay-profile-hero-480.png`;
const portraitUrl768 = `${portraitBaseUrl}/vijay-profile-hero-768.png`;

// Floating canvas elements. Positions chosen to avoid the central photo silhouette
// (photo occupies roughly horizontal 38–62% and vertical 18–82% of the canvas).
const floats = [
  { key: 'about', type: 'iconwin-about', top: '20%', left: '11%', rotate: -3, delay: 0.20, dur: 7.5 },
  { key: 'works', type: 'iconwin-works', top: '15%', right: '11%', rotate: 3, delay: 0.28, dur: 8.0 },
  {
    key: 'stickerA', type: 'sticker', top: '3%', left: '41%', rotate: -2, delay: 0.36, dur: 8.5,
    text: 'HARVARDXR \'25 WINNER', variant: 'fill'
  },
  {
    key: 'stickerB', type: 'sticker', top: '55%', right: 'calc(9% - 55px)', rotate: 4, delay: 0.44, dur: 7.0,
    text: '80K+ INSTALLS VR GAME', variant: 'fill'
  },
  { key: 'smiley', type: 'smiley', bottom: '4%', right: '16%', rotate: 0, delay: 0.60, dur: 0 },
  {
    key: 'stickerD', type: 'sticker', bottom: '25%', left: '15%', rotate: -2, delay: 0.68, dur: 8.0,
    text: 'XR BOOTCAMP HIGH HONORS', variant: 'fill'
  },
];

const mountFrom = (delay) => ({
  initial: { opacity: 0, scale: 0.4, x: 0, y: 0 },
  whileInView: { opacity: 1, scale: 1, x: 0, y: 0 },
  viewport: { once: true, margin: '-10%' },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

const idleFloat = (dur) =>
  dur > 0
    ? { animate: { y: [0, -4, 0] }, transition: { duration: dur, repeat: Infinity, ease: 'easeInOut', delay: 1.2 } }
    : {};

function Hero() {
  const handleViewMoreClick = (event) => {
    event.preventDefault();
    document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="v3-hero" id="hero">
      <div className="v3-hero__inner">
        <div className="v3-hero-canvas">
          {/* Central photo */}
          <motion.div
            className="v3-hero-canvas__photo"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src={portraitUrl768}
              srcSet={`${portraitUrl480} 480w, ${portraitUrl768} 768w`}
              sizes="(max-width: 768px) 220px, 330px"
              alt="Vijay Kumar Kagupati"
              width="768"
              height="768"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </motion.div>

          {/* Floating satellites */}
          {floats.map((f) => {
            const isClickable = f.type === 'iconwin-about' || f.type === 'iconwin-works';
            const pos = {};
            if (f.top !== undefined) pos.top = f.top;
            if (f.bottom !== undefined) pos.bottom = f.bottom;
            if (f.left !== undefined) pos.left = f.left;
            if (f.right !== undefined) pos.right = f.right;
            const baseStyle = { ...pos, '--rot': `${f.rotate}deg` };

            let inner;
            if (f.type === 'iconwin-about') {
              inner = (
                <IconWindow
                  href="/#about"
                  icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-6 8-6s8 2 8 6" /></svg>}
                  label="About Me"
                />
              );
            } else if (f.type === 'iconwin-works') {
              inner = (
                <IconWindow
                  href="/#portfolio"
                  header="XR_PROJECTS.DIR"
                  icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9 12h6M9 16h6" /></svg>}
                  label="My Works"
                />
              );
            } else if (f.type === 'floppy') {
              inner = <Floppy label="SAVE" tilt={0} size="120px" />;
            } else if (f.type === 'smiley') {
              inner = (
                <motion.div
                  animate={{ rotate: [-3, 3, -3] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
                >
                  <Smiley color="var(--cobalt)" size={96} />
                </motion.div>
              );
            } else if (f.type === 'sticker') {
              inner = <AchievementSticker text={f.text} variant={f.variant} />;
            }

            return (
              <motion.div
                key={f.key}
                className={`v3-hero-canvas__float ${isClickable ? 'v3-hero-canvas__float--clickable' : 'v3-hero-canvas__float--decorative'}`}
                data-float-key={f.key}
                style={baseStyle}
                {...mountFrom(f.delay)}
              >
                <motion.div className="v3-hero-canvas__rot" {...idleFloat(f.dur)}>
                  <div
                    className="v3-hero-canvas__interaction"
                    data-hover-label={isClickable ? 'Click to learn more' : undefined}
                  >
                    {inner}
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Big headline */}
        <motion.h1
          className="v3-hero-headline"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.6, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
        >
          I build immersive experiences.
        </motion.h1>

        <motion.p
          className="v3-hero-sub"
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.5, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
        >
          and I measure what holds people's <u>attention</u>.
        </motion.p>

        <motion.a
          href="#portfolio"
          className="v3-hero-scroll"
          onClick={handleViewMoreClick}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.5, delay: 1.3 }}
        >
          SCROLL DOWN TO VIEW MORE
          <motion.span
            className="v3-hero-scroll__arrow"
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            ↓
          </motion.span>
        </motion.a>
      </div>
    </section>
  );
}

export default Hero;
